<!-- 角色权限展示组件 -->
<template>
  <div class="role-permission">
    <div v-if="!selectedRoleId" class="empty-tip">
      <i class="el-icon-info"></i>
      <p>请先在左侧选择一个角色</p>
    </div>

    <div v-else class="permission-content">
      <div class="permission-body">
        <!-- 角色信息：头像 + 表单 -->
        <div class="role-info-section">
          <div class="role-avatar">
            <i class="el-icon-user-solid"></i>
          </div>
          <el-form
            :model="roleForm"
            label-position="top"
            class="role-form"
          >
            <el-row :gutter="24">
              <el-col :span="12">
                <el-form-item label="角色名称" required>
                  <el-input
                    v-model="roleForm.role_name"
                    placeholder="请输入角色名称"
                  ></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="状态" required>
                  <el-select
                    v-model="roleForm.role_status"
                    placeholder="请选择状态"
                    style="width: 100%"
                  >
                    <el-option label="启用" :value="0"></el-option>
                    <el-option label="禁用" :value="1"></el-option>
                  </el-select>
                </el-form-item>
              </el-col>
            </el-row>
          </el-form>
        </div>

        <!-- 权限管理 -->
        <div class="perm-section">
          <div class="perm-section__title">
            <div class="perm-section__title-left">
              <span class="perm-section__title-text">
                <i class="el-icon-menu"></i>权限管理
              </span>
              <button
                type="button"
                class="perm-all-btn"
                :class="{ 'is-active': checkAll || isIndeterminate }"
                @click="toggleCheckAll"
              >
                <i class="el-icon-check" v-show="checkAll || isIndeterminate"></i>
                {{ checkAll ? '取消全选' : '全选' }}
              </button>
            </div>
            <div class="perm-section__title-right">
              <span class="perm-count">已选 {{ selectedLeafCount }} / {{ totalLeafCount }} 项</span>
              <el-button
                type="primary"
                size="small"
                @click="saveRoleInfo"
                :loading="saving"
              >保存</el-button>
            </div>
          </div>

          <div
            class="perm-group"
            v-for="firstLevel in navData"
            :key="firstLevel.id"
          >
            <!-- 组横条 -->
            <div
              :class="[
                'perm-group__bar',
                { 'is-checked': isFirstLevelChecked(firstLevel) }
              ]"
            >
              <div class="perm-group__bar-left">
                <i v-if="firstLevel.icon" :class="firstLevel.icon" class="group-icon"></i>
                <el-checkbox
                  :value="isFirstLevelChecked(firstLevel)"
                  @change="handleFirstLevelChange(firstLevel, $event)"
                >{{ firstLevel.label }}</el-checkbox>
              </div>
              <span class="perm-group__count">
                {{ selectedInGroup(firstLevel) }} / {{ firstLevel.children ? firstLevel.children.length : 0 }}
              </span>
            </div>

            <!-- 子项：圆形勾选框横向排列 -->
            <div class="perm-group__items">
              <el-checkbox
                v-for="secondLevel in firstLevel.children"
                :key="secondLevel.id"
                :value="isSecondLevelChecked(secondLevel)"
                @change="handleSecondLevelChange(firstLevel, secondLevel, $event)"
                class="perm-item"
              >{{ secondLevel.label }}</el-checkbox>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { updateApi } from "@/services/admin_role/index.js";

export default {
  name: "RolePermission",
  props: {
    selectedRoleId: {
      type: Number,
      default: null
    },
    selectedRole: {
      type: Object,
      default: null
    },
    selectedRolePermissions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      navData: [],
      checkedKeys: [],
      roleForm: {
        role_name: '',
        role_status: 0
      },
      saving: false,
      checkAll: false,
      isIndeterminate: false,
    };
  },
  watch: {
    selectedRole: {
      handler(newVal) {
        if (newVal) {
          this.roleForm = {
            role_name: newVal.role_name || '',
            role_status: newVal.role_status !== undefined ? newVal.role_status : 0
          };
        }
      },
      immediate: true,
      deep: true
    },
    selectedRolePermissions: {
      handler(newVal) {
        // 保持字符串类型的ID数组，因为后端返回的格式如 "0,1,1-1,1-2"
        // navList中的id也是字符串格式，如 "0", "1", "1-1"
        this.checkedKeys = (newVal || []).map(id => String(id));
        this.$nextTick(() => {
          this.updateCheckAllStatus();
        });
      },
      immediate: true,
      deep: true
    }
  },
  computed: {
    ...mapState(["userInfo"]),
    totalLeafCount() {
      let total = 0;
      this.navData.forEach(f => {
        total += (f.children || []).length;
      });
      return total;
    },
    selectedLeafCount() {
      let count = 0;
      this.navData.forEach(f => {
        (f.children || []).forEach(s => {
          if (this.checkedKeys.includes(s.id)) count++;
        });
      });
      return count;
    }
  },
  created() {
    this.navData = this.$store.state.navList;
  },
  methods: {
    /* 更新全选状态 */
    updateCheckAllStatus() {
      const allIds = this.getAllPermissionIds();
      const totalCount = allIds.length;

      if (totalCount === 0) {
        this.checkAll = false;
        this.isIndeterminate = false;
        return;
      }

      const checkedInCurrentNav = allIds.filter(id => this.checkedKeys.includes(id)).length;

      this.checkAll = checkedInCurrentNav === totalCount;
      this.isIndeterminate = checkedInCurrentNav > 0 && checkedInCurrentNav < totalCount;
    },

    /* 全选 / 取消全选 */
    handleCheckAllChange(checked) {
      if (checked) {
        const ids = this.getAllPermissionIds();
        this.checkedKeys = Array.from(new Set([...this.checkedKeys, ...ids]));
      } else {
        this.checkedKeys = [];
      }
      this.updateCheckAllStatus();
    },

    /* 点击全选按钮：根据当前状态切换 */
    toggleCheckAll() {
      this.handleCheckAllChange(!this.checkAll);
    },

    /* 某分组已选子项数量 */
    selectedInGroup(firstLevel) {
      if (!firstLevel.children) return 0;
      return firstLevel.children.filter(s => this.checkedKeys.includes(s.id)).length;
    },

    /* 获取所有权限ID */
    getAllPermissionIds() {
      const ids = [];
      this.navData.forEach(firstLevel => {
        ids.push(firstLevel.id);
        if (firstLevel.children) {
          firstLevel.children.forEach(secondLevel => {
            ids.push(secondLevel.id);
          });
        }
      });
      return ids;
    },

    /* 一级菜单勾选变化 */
    handleFirstLevelChange(firstLevel, checked) {
      if (checked) {
        if (!this.checkedKeys.includes(firstLevel.id)) {
          this.checkedKeys.push(firstLevel.id);
        }
        if (firstLevel.children) {
          firstLevel.children.forEach(secondLevel => {
            if (!this.checkedKeys.includes(secondLevel.id)) {
              this.checkedKeys.push(secondLevel.id);
            }
          });
        }
      } else {
        this.checkedKeys = this.checkedKeys.filter(id => id !== firstLevel.id);
        if (firstLevel.children) {
          firstLevel.children.forEach(secondLevel => {
            this.checkedKeys = this.checkedKeys.filter(id => id !== secondLevel.id);
          });
        }
      }
      this.updateCheckAllStatus();
    },

    /* 二级菜单勾选变化 */
    handleSecondLevelChange(firstLevel, secondLevel, checked) {
      if (checked) {
        if (!this.checkedKeys.includes(secondLevel.id)) {
          this.checkedKeys.push(secondLevel.id);
        }
        // 同步勾选父级
        if (!this.checkedKeys.includes(firstLevel.id)) {
          this.checkedKeys.push(firstLevel.id);
        }
      } else {
        this.checkedKeys = this.checkedKeys.filter(id => id !== secondLevel.id);
        // 父级下无其他选中子项时，取消父级
        const hasOtherChecked = (firstLevel.children || []).some(child =>
          child.id !== secondLevel.id && this.checkedKeys.includes(child.id)
        );
        if (!hasOtherChecked) {
          this.checkedKeys = this.checkedKeys.filter(id => id !== firstLevel.id);
        }
      }
      this.updateCheckAllStatus();
    },

    /* 判断一级菜单是否选中 */
    isFirstLevelChecked(firstLevel) {
      return this.checkedKeys.includes(firstLevel.id);
    },

    /* 判断二级菜单是否选中 */
    isSecondLevelChecked(secondLevel) {
      return this.checkedKeys.includes(secondLevel.id);
    },

    /* 保存角色信息 */
    async saveRoleInfo() {
      const role_menu = this.checkedKeys.join(",");

      if (!this.roleForm.role_name) {
        this.$message.error('请输入角色名称');
        return;
      }

      if (this.roleForm.role_status !== 0 && this.roleForm.role_status !== 1) {
        this.$message.error('请选择状态');
        return;
      }

      if (!role_menu) {
        this.$message.error('请至少选择一个权限');
        return;
      }

      this.saving = true;

      try {
        const res = await updateApi({
          id: this.selectedRoleId,
          role_name: this.roleForm.role_name,
          role_status: this.roleForm.role_status,
          role_menu,
        });

        if (res.code === 200) {
          this.$message.success('保存成功');

          this.$emit('role-updated', {
            id: this.selectedRoleId,
            role_name: this.roleForm.role_name,
            role_status: this.roleForm.role_status,
            role_menu,
          });

          this.$root.$emit('role-list-updated');
        }
      } catch (error) {
        this.$message.error('保存失败');
      } finally {
        this.saving = false;
      }
    },
  }
};
</script>

<style scoped lang="less">
.role-permission {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 空状态提示 */
.empty-tip {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #86909c;

  i {
    font-size: 72px;
    margin-bottom: 16px;
    color: #e4e7ed;
  }

  p {
    font-size: 15px;
    margin: 0;
    letter-spacing: 0.5px;
  }
}

/* 权限内容 */
.permission-content {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 内容滚动区 */
.permission-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 20px 24px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: #dcdcdc;
    border-radius: 4px;

    &:hover {
      background: #c0c0c0;
    }
  }
}

/* 角色信息区：头像 + 表单（卡片化，与下方权限卡片同款） */
.role-info-section {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  background: #fff;
  border: 1px solid #eef0f3;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(17, 24, 39, 0.04);
}

.role-avatar {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 8px;
  background: linear-gradient(135deg, #3d8dfc 0%, #6a8dff 100%);
  box-shadow: 0 4px 12px rgba(61, 141, 252, 0.28);
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    color: #fff;
    font-size: 28px;
  }
}

.role-form {
  flex: 1;
  min-width: 0;

  /deep/ .el-form-item {
    margin-bottom: 0;
  }

  /deep/ .el-form-item__label {
    font-size: 15px;
    font-weight: 600;
    color: #282828;
    line-height: 28px;
    padding-bottom: 6px;
  }

  /deep/ .el-input__inner {
    height: 42px;
    line-height: 42px;
    border-radius: 6px;
  }
}

/* 权限管理 */
.perm-section {
  margin-top: 28px;
}

.perm-section__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;

  .perm-section__title-left {
    display: flex;
    align-items: center;
    gap: 18px;
  }

  .perm-section__title-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .perm-count {
    font-size: 13px;
    color: #86909c;
  }

  .perm-all-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 12px;
    font-size: 13px;
    line-height: 1;
    color: #4e5969;
    background: #fff;
    border: 1px solid #e5e6eb;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      color: #3d8dfc;
      border-color: #bcd4ff;
      background: #f2f7ff;
    }

    &.is-active {
      color: #3d8dfc;
      border-color: #3d8dfc;
      background: #eef4ff;
    }

    i {
      font-size: 12px;
    }
  }

  .perm-section__title-text {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 17px;
    font-weight: 700;
    color: #1d2129;

    i {
      font-size: 18px;
      color: #3d8dfc;
    }
  }
}

/* 分组卡片：表头行 + 下方子项铺满 */
.perm-group {
  margin-bottom: 12px;
  background: #fff;
  border: 1px solid #eef0f3;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(17, 24, 39, 0.04);
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: #d6e4ff;
    box-shadow: 0 4px 14px rgba(61, 141, 252, 0.1);
  }
}

.perm-group__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f7f9fc;
  border-bottom: 1px solid #eef0f3;
  transition: background 0.2s;

  .perm-group__bar-left {
    display: flex;
    align-items: center;

    .group-icon {
      flex-shrink: 0;
      font-size: 16px;
      color: #9aa3b2;
      margin-right: 8px;
      transition: color 0.2s;
    }

    /deep/ .el-checkbox {
      display: flex;
      align-items: center;

      .el-checkbox__label {
        font-size: 14px;
        font-weight: 600;
        color: #282828;
        white-space: nowrap;
      }
    }
  }

  .perm-group__count {
    flex-shrink: 0;
    font-size: 12px;
    color: #a9b0bd;
    background: #fff;
    border: 1px solid #eef0f3;
    border-radius: 10px;
    padding: 1px 9px;
    transition: all 0.2s;
  }

  &.is-checked {
    background: #eef4ff;

    .group-icon {
      color: #3d8dfc;
    }

    /deep/ .el-checkbox__label {
      color: #3d8dfc;
    }

    .perm-group__count {
      color: #3d8dfc;
      border-color: #cfe0ff;
    }
  }
}

/* 子项：圆角标签(chip)勾选框，铺满整行 */
.perm-group__items {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 12px;
  padding: 14px 16px;

  .perm-item {
    margin: 0;
    display: flex;
    align-items: center;

    /deep/ .el-checkbox {
      margin-right: 0;
      height: auto;
      border: 1px solid #e6e8eb;
      border-radius: 16px;
      padding: 6px 14px 6px 10px;
      background: #f7f8fa;
      cursor: pointer;
      transition: all 0.2s;
    }

    /deep/ .el-checkbox:hover {
      border-color: #bcd4ff;
      background: #f2f7ff;
    }

    /deep/ .el-checkbox__label {
      font-size: 14px;
      color: #4e5969;
      padding-left: 8px;
    }

    /deep/ .el-checkbox.is-checked {
      background: #eef4ff;
      border-color: #3d8dfc;
    }

    /deep/ .el-checkbox.is-checked:hover {
      background: #e3eeff;
    }

    /deep/ .el-checkbox.is-checked .el-checkbox__label {
      color: #3d8dfc;
    }
  }
}
</style>
